import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import QrCodeOutlinedIcon from "@mui/icons-material/QrCodeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import {
  archiveTracker,
  deleteTracker,
  duplicateTracker,
  getCategoryLabel,
  getKindLabel,
  getTrackerAccessLabel,
  getTrackerListMetaParts,
  getTrackerPublicUrl,
  getTrackers,
  isTrackerPublicLinkAvailable,
  normalizeTrackerAccess,
  TRACKER_ACCESS,
  TRACKER_STATUS,
} from "./trackerStorage";
import {
  countPeopleForTracker,
  countReviewsDue,
  countSubmissionsForTracker,
  countUniquePeople,
  getSubmissions,
} from "./submissionStorage";
import { reloadDemoJourneyTrackers } from "./journeyDemoData";
import { getQuestions } from "./questions/questionStorage";
import {
  coachAvatarColor,
  coachDisplayName,
  coachInitials,
} from "./AddCoachesPopup";
import TrackerQrCodePopup from "./TrackerQrCodePopup";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint, onClick }) => (
  <Box
    component={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
    sx={{
      flex: "1 1 140px",
      minWidth: 130,
      p: 1.75,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      textAlign: "left",
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": onClick
        ? {
            borderColor: PRIMARY,
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          }
        : undefined,
    }}
  >
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 24,
        fontWeight: 700,
        color: "#0f172a",
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.5, fontSize: 11.5, color: MUTED }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const CountLink = ({ value, onClick }) => (
  <Typography
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    sx={{
      border: 0,
      background: "none",
      p: 0,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 700,
      color: PRIMARY,
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {value}
  </Typography>
);

const CoachStack = ({ coaches = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (!coaches.length) {
    return (
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>None</Typography>
    );
  }

  const shown = coaches.slice(0, 4);
  const extra = coaches.length - shown.length;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          maxWidth: 220,
          border: 0,
          background: "none",
          p: 0,
          cursor: "pointer",
          textAlign: "left",
          "&:hover .coach-count-label": { color: PRIMARY },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {shown.map((coach, index) => (
            <Avatar
              key={coach.id || index}
              sx={{
                width: 28,
                height: 28,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: coachAvatarColor(coach),
                border: "2px solid #fff",
                ml: index === 0 ? 0 : -0.85,
                zIndex: shown.length - index,
              }}
            >
              {coachInitials(coach)}
            </Avatar>
          ))}
          {extra > 0 ? (
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: "#e2e8f0",
                color: "#334155",
                border: "2px solid #fff",
                ml: -0.85,
              }}
            >
              +{extra}
            </Avatar>
          ) : null}
        </Box>
        <Typography
          className="coach-count-label"
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            transition: "color 0.15s ease",
          }}
        >
          {coaches.length === 1
            ? coachDisplayName(coaches[0])
            : `${coaches.length} coaches`}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            minWidth: 260,
            maxWidth: 320,
            maxHeight: 320,
            overflowY: "auto",
            borderRadius: "12px",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <Box sx={{ px: 1.75, pt: 1.5, pb: 1 }}>
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: MUTED, mb: 1 }}
          >
            Selected coaches ({coaches.length})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {coaches.map((coach, index) => (
              <Box
                key={coach.id || index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.1,
                  px: 0.75,
                  py: 0.75,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: coachAvatarColor(coach),
                  }}
                >
                  {coachInitials(coach)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {coachDisplayName(coach)}
                  </Typography>
                  {coach.email ? (
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: MUTED,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {coach.email}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const ProgrammeCell = ({ row }) => {
  const access = normalizeTrackerAccess(row.trackerAccess, row);
  const programmes = row.programmes || [];

  if (access === TRACKER_ACCESS.PUBLIC) {
    return (
      <Chip
        size="small"
        label="Public"
        sx={{
          height: 24,
          fontWeight: 700,
          fontSize: 11.5,
          bgcolor: PRIMARY_SOFT,
          color: PRIMARY,
        }}
      />
    );
  }

  if (!programmes.length) {
    return (
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a" }}>
          GymRite members
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: MUTED }}>
          No programme selected
        </Typography>
      </Box>
    );
  }

  const shown = programmes.slice(0, 2);
  const extra = programmes.length - shown.length;
  const allTitles = programmes.map((p) => p.title || p).join(", ");

  return (
    <Tooltip title={allTitles} arrow placement="top">
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 220 }}>
        {shown.map((item) => (
          <Chip
            key={item.id || item.title || item}
            size="small"
            label={item.title || item}
            sx={{
              height: 24,
              maxWidth: 140,
              fontWeight: 600,
              fontSize: 11.5,
              bgcolor: "#f8fafc",
              border: `1px solid ${BORDER}`,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ))}
        {extra > 0 ? (
          <Chip
            size="small"
            label={`+${extra}`}
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: 11.5,
              bgcolor: PRIMARY_SOFT,
              color: PRIMARY,
            }}
          />
        ) : null}
      </Box>
    </Tooltip>
  );
};

export default function TrackersList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [trackers, setTrackers] = useState([]);
  const [search, setSearch] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [qrTracker, setQrTracker] = useState(null);

  const loadTrackers = () => {
    setTrackers(getTrackers());
  };

  useEffect(() => {
    loadTrackers();
  }, []);

  const handleDelete = () => {
    if (!deleteDoc?.id) return;
    deleteTracker(deleteDoc.id);
    setOpenDelete(false);
    setDeleteDoc(null);
    loadTrackers();
    enqueueSnackbar("Tracker deleted", { variant: "success" });
  };

  const openDetail = (row) => {
    if (!row?.id) return;
    navigate(`/trackers/${row.id}`);
  };

  const copyPublicLink = async (row) => {
    const url = getTrackerPublicUrl(row);
    if (!url) {
      enqueueSnackbar("Public link not available for this tracker", {
        variant: "info",
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar("Public link copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy link", { variant: "error" });
    }
  };

  const MENU_OPTIONS = [
    {
      label: "View details",
      icon: "akar-icons:eye",
      handleClick: (row) => openDetail(row),
    },
    {
      label: "Submissions",
      icon: "mdi:inbox-outline",
      handleClick: (row) => {
        if (!row?.id) return;
        navigate(`/tracker-submissions?tracker=${encodeURIComponent(row.id)}`);
      },
    },
    {
      label: "Copy public link",
      icon: "akar-icons:link-chain",
      handleClick: (row) => copyPublicLink(row),
      forPublicOnly: true,
    },
    {
      label: "View QR code",
      icon: "mdi:qrcode",
      handleClick: (row) => setQrTracker(row),
      forPublicOnly: true,
    },
    {
      label: "Duplicate",
      icon: "akar-icons:copy",
      handleClick: (row) => {
        const copy = duplicateTracker(row.id);
        if (!copy) {
          enqueueSnackbar("Could not duplicate tracker", { variant: "error" });
          return;
        }
        enqueueSnackbar("Journey duplicated", { variant: "success" });
        loadTrackers();
        navigate(`/trackers/${copy.id}`);
      },
    },
    {
      label: "Archive",
      icon: "mdi:archive-outline",
      handleClick: (row) => {
        if (row.status === TRACKER_STATUS.ARCHIVED) {
          enqueueSnackbar("Already archived", { variant: "info" });
          return;
        }
        archiveTracker(row.id);
        loadTrackers();
        enqueueSnackbar("Journey archived — intake stopped", {
          variant: "success",
        });
      },
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const submissions = useMemo(() => getSubmissions(), [trackers]);

  const stats = useMemo(() => {
    const live = trackers.filter(
      (row) =>
        row.status === TRACKER_STATUS.LIVE || row.status === "published"
    ).length;
    const draftOrOther = trackers.length - live;
    const publicLinks = trackers.filter((row) =>
      isTrackerPublicLinkAvailable(row)
    ).length;
    return {
      trackers: trackers.length,
      live,
      draftOrOther,
      people: countUniquePeople(submissions),
      reviewsDue: countReviewsDue(submissions),
      publicLinks,
    };
  }, [trackers, submissions]);

  const openSubmissions = (trackerId) => {
    if (!trackerId) return;
    navigate(`/tracker-submissions?tracker=${encodeURIComponent(trackerId)}`);
  };

  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? trackers
      : trackers.filter((row) => {
          const coaches = (row.coaches || [])
            .map((c) => coachDisplayName(c))
            .join(" ");
          const programmes = (row.programmes || [])
            .map((p) => p.title || p)
            .join(" ");
          const hay =
            `${row.name || ""} ${row.description || ""} ${coaches} ${programmes} ${getTrackerAccessLabel(
              row.trackerAccess,
              row
            )} ${getCategoryLabel(row.category)} ${getKindLabel(row.kind)}`.toLowerCase();
          return hay.includes(q);
        });

    return filtered.map((row) => ({
      ...row,
      peopleCount: countPeopleForTracker(row.id),
      submissionsCount: countSubmissionsForTracker(row.id),
      MENU_OPTIONS: MENU_OPTIONS.filter(
        (item) =>
          !item.forPublicOnly || isTrackerPublicLinkAvailable(row)
      ),
    }));
  }, [trackers, search, submissions]);

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "name",
      label: "Name",
      renderData: (row) => {
        const metaParts = getTrackerListMetaParts(row).filter(
          (item) => item.label !== "Forms" && item.label !== "Coaches"
        );
        const lines =
          metaParts.length > 0
            ? metaParts
            : [
                {
                  label: "Type",
                  value: `${getKindLabel(row.kind)} · ${getCategoryLabel(
                    row.category
                  )}`,
                },
              ];
        return (
          <Stack
            direction="column"
            alignItems="start"
            spacing={0}
            onClick={(e) => {
              e.stopPropagation();
              openDetail(row);
            }}
            sx={{
              cursor: "pointer",
              py: 0.25,
              "&:hover .tracker-name-link": {
                color: PRIMARY,
              },
            }}
          >
            <Typography
              className="tracker-name-link"
              variant="subtitle2"
              noWrap
              sx={{
                fontWeight: 700,
                transition: "color 0.15s ease",
                mb: 0.45,
              }}
            >
              {row.name || "Untitled Tracker"}
            </Typography>
            {lines.map((item) => (
              <Typography
                key={`${item.label}-${item.value}`}
                sx={{
                  fontSize: 11.5,
                  lineHeight: 1.45,
                  display: "block",
                  color: "text.secondary",
                }}
                variant="subtitle2"
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    opacity: 0.85,
                    mr: 0.5,
                  }}
                >
                  {item.label}:
                </Box>
                <Box component="span" sx={{ opacity: 0.9 }}>
                  {item.value}
                </Box>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      id: "people",
      label: "People",
      renderData: (row) => (
        <CountLink
          value={row.peopleCount ?? 0}
          onClick={() => openSubmissions(row.id)}
        />
      ),
    },
    {
      id: "submissions",
      label: "Submissions",
      renderData: (row) => (
        <CountLink
          value={row.submissionsCount ?? 0}
          onClick={() => openSubmissions(row.id)}
        />
      ),
    },
    {
      id: "coaches",
      label: "Coaches",
      renderData: (row) => <CoachStack coaches={row.coaches || []} />,
    },
    {
      id: "programmes",
      label: "Programme / Access",
      renderData: (row) => <ProgrammeCell row={row} />,
    },
    {
      id: "publicLink",
      label: "Public link",
      renderData: (row) => {
        const isPublic = isTrackerPublicLinkAvailable(row);
        if (!isPublic) {
          return (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              {getTrackerAccessLabel(row.trackerAccess, row)}
            </Typography>
          );
        }
        return (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Copy public link">
              <IconButton
                size="small"
                onClick={() => copyPublicLink(row)}
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: "8px",
                  bgcolor: "#fff",
                  "&:hover": {
                    bgcolor: PRIMARY_SOFT,
                    borderColor: PRIMARY,
                    color: PRIMARY,
                  },
                }}
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View QR code">
              <IconButton
                size="small"
                onClick={() => setQrTracker(row)}
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: "8px",
                  bgcolor: "#fff",
                  "&:hover": {
                    bgcolor: PRIMARY_SOFT,
                    borderColor: PRIMARY,
                    color: PRIMARY,
                  },
                }}
              >
                <QrCodeOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <Label
          variant="ghost"
          color={
            row.status === TRACKER_STATUS.LIVE || row.status === "published"
              ? "success"
              : row.status === TRACKER_STATUS.ARCHIVED
                ? "default"
                : "warning"
          }
        >
          {row.status === TRACKER_STATUS.LIVE || row.status === "published"
            ? "Live"
            : row.status === TRACKER_STATUS.ARCHIVED
              ? "Archived"
              : "Draft"}
        </Label>
      ),
    },
    {
      id: "updatedAt",
      label: "Updated",
      renderData: (row) =>
        row.updatedAt ? moment(row.updatedAt).format("DD/MM/YYYY HH:mm") : "—",
    },
  ];

  const qrUrl = qrTracker ? getTrackerPublicUrl(qrTracker) : "";

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Are you sure you want to delete this Tracker?"
        handleAgree={handleDelete}
      />

      <TrackerQrCodePopup
        open={Boolean(qrTracker)}
        url={qrUrl}
        trackerName={qrTracker?.name}
        onClose={() => setQrTracker(null)}
      />

      <div className="container-fluid tracker-module-page">
        <div className="row">
          <div className="col-lg-5 col-sm-12" style={{ marginTop: 10 }}>
            <h2>Trackers</h2>
            {/* <Typography sx={{ color: "text.secondary", mb: 1, fontSize: 14 }}>
              Every form the gym runs and every ongoing journey. Open a name to
              see details; edit from there.
            </Typography> */}
          </div>
          <div className="col-lg-7 col-sm-12">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
                marginBottom: 1,
                marginTop: 1,
              }}
            >
              <TextField
                size="small"
                label="Search"
                placeholder="Name, coach, programme…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", sm: 240 },
                  flexShrink: 0,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />

              <button
                className="small-contained-button"
                onClick={() => navigate("/trackers/new")}
              >
                New tracker
              </button>
            </Box>
          </div>
        </div>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.25,
            mb: 2.25,
          }}
        >
          <StatCard
            label="Trackers"
            value={stats.trackers}
            hint="GymRite Trackers"
          />
          <StatCard
            label="Live"
            value={stats.live}
            hint={
              stats.draftOrOther > 0
                ? `${stats.draftOrOther} not published`
                : "All published"
            }
          />
          <StatCard
            label="People"
            value={stats.people}
            hint="on a tracker"
            onClick={() => navigate("/tracker-submissions")}
          />
          <StatCard
            label="Reviews due"
            value={stats.reviewsDue}
            hint="Waiting on a coach"
            onClick={() => navigate("/tracker-submissions")}
          />
          <StatCard
            label="Public links"
            value={stats.publicLinks}
            hint="Sign in, then the form"
          />
        </Box>

        <CustomMUITable
          TABLE_HEAD={TABLE_HEAD}
          data={tableRows}
          MENU_OPTIONS={MENU_OPTIONS}
          hide_search
          className="card-with-background tracker-module-table"
        />
      </div>
    </>
  );
}
